import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    HandCoins, ChevronRight, ChevronDown, RefreshCw, Download, Loader2, AlertTriangle, Filter, Search,
} from 'lucide-react';

import {
    fetchAdvanceReportCCs, fetchAdvanceReport, clearAdvanceReport,
} from '../../slices/labourAdvanceSlice/labourAdvanceSlice';
import {
    defaultAdvanceReportRange, advanceReportRangeError, buildAdvanceReportParams,
    advanceReportLinesFor, advanceReportTotals,
} from '../HR/labourAdvanceHelpers';

// Labour Advance Report (MOID 704, /HR/LabourAdvanceReport) - advance ledger per Labour + CC for a month range
// (spGetLabourAdvanceReport). Closing = the unsettled advance the next payroll recovers.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 3 + i);
const LABOUR_TYPES = [
    { value: 'All', label: 'All' },
    { value: 'Own Labour', label: 'Own Labour' },
    { value: 'Contractor', label: 'Contractor' },
];

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed';

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const period = (m, y) => `${MONTH_ABBR[(m || 1) - 1]}-${y}`;

const Label = ({ children }) => (
    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{children}</label>
);

const blankFilters = () => ({
    ccCode: 'All', ...defaultAdvanceReportRange(), labourType: 'All', labourId: '', onlyWithBalance: false,
});

// Advance batches + payroll recoveries behind one ledger row
const LedgerLines = ({ lines }) => {
    if (!lines.length) return <div className="py-2 px-3 text-gray-400">No advance or recovery in this period (opening balance only).</div>;
    const th = 'px-2 py-1 text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase whitespace-nowrap';
    const td = 'px-2 py-1 text-[11px] whitespace-nowrap';
    return (
        <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
            <thead className="bg-indigo-50 dark:bg-indigo-900/20">
                <tr>
                    {['Type', 'Month', 'Date', 'Ref No', 'Status'].map((h) => <th key={h} className={`${th} text-left`}>{h}</th>)}
                    {['Amount', 'Paid by CMS'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {lines.map((l, i) => {
                    const isAdv = l.LineType === 'Advance';
                    return (
                        <tr key={i}>
                            <td className={td}>
                                <span className={`px-1.5 py-0.5 rounded font-semibold ${isAdv
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                    : 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'}`}>
                                    {isAdv ? 'Advance' : 'Recovery'}
                                </span>
                            </td>
                            <td className={td}>{period(l.Month, l.Year)}</td>
                            <td className={td}>{l.LineDate}</td>
                            <td className={td}>{l.RefNo}</td>
                            <td className={`${td} ${l.LineStatus === 'Approved' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>{l.LineStatus}</td>
                            <td className={`${td} text-right font-semibold`}>{isAdv ? '' : '− '}{money(l.Amount)}</td>
                            <td className={`${td} text-right`}>{isAdv ? money(l.CMSPaid) : ''}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

const LabourAdvanceReport = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const userId = userData?.userId || 0;
    const { reportCCs, reportCCsLoading, report, reportLoading, reportError } = useSelector((s) => s.labourAdvance);

    // Filters live only in local state (no Redux sync effect, see the report-page filter sync bug).
    const [filters, setFilters] = useState(blankFilters);
    const [ranParams, setRanParams] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (roleId) dispatch(fetchAdvanceReportCCs({ roleId, userId }));
        return () => dispatch(clearAdvanceReport());
    }, [dispatch, roleId, userId]);

    const rows  = useMemo(() => report?.Rows  || [], [report]);
    const lines = useMemo(() => report?.Lines || [], [report]);
    const totals = useMemo(() => advanceReportTotals(rows), [rows]);

    const setField = (k, v) => {
        setFilters((p) => ({ ...p, [k]: v }));
        setRanParams(null); setExpanded(null); dispatch(clearAdvanceReport());
    };

    const runReport = () => {
        const err = advanceReportRangeError(filters);
        if (err) { toast.warning(err); return; }
        const params = buildAdvanceReportParams(filters, roleId, userId);
        setRanParams(params);
        setExpanded(null);
        dispatch(fetchAdvanceReport(params));
    };

    const handleRefresh = () => { if (ranParams) { setExpanded(null); dispatch(fetchAdvanceReport(ranParams)); } };

    const handleReset = () => {
        setFilters(blankFilters()); setRanParams(null); setExpanded(null); dispatch(clearAdvanceReport());
    };

    const handleExport = () => {
        if (!rows.length) { toast.warning('No data to export'); return; }
        const ledger = rows.map((r) => ({
            'CC Code': r.CCCode, 'CC Name': r.CCName, 'Labour ID': r.LabourId, 'Labour Name': r.LabourName,
            'Labour Type': r.LabourType, Contractor: r.ContractorName || r.ContractorCode || '',
            Opening: num(r.Opening), 'Advance Given': num(r.AdvanceGiven), 'Given - in Approval': num(r.AdvancePending),
            'Paid by CMS': num(r.CMSPaid), Recovered: num(r.Recovered), 'Recovered - Payroll in Approval': num(r.RecoveryPending),
            Closing: num(r.Closing),
        }));
        ledger.push({
            'CC Code': 'TOTAL', Opening: totals.Opening, 'Advance Given': totals.AdvanceGiven, 'Given - in Approval': totals.AdvancePending,
            'Paid by CMS': totals.CMSPaid, Recovered: totals.Recovered, 'Recovered - Payroll in Approval': totals.RecoveryPending, Closing: totals.Closing,
        });
        const detail = lines.map((l) => ({
            Type: l.LineType, 'CC Code': l.CCCode, 'Labour ID': l.LabourId, 'Labour Name': l.LabourName, 'Labour Type': l.LabourType,
            Month: period(l.Month, l.Year), Date: l.LineDate, 'Ref No': l.RefNo, Status: l.LineStatus,
            Amount: num(l.Amount), 'Paid by CMS': l.LineType === 'Advance' ? num(l.CMSPaid) : '',
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ledger), 'Advance Ledger');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail.length ? detail : [{ Type: 'No lines' }]), 'Advance & Recovery Lines');
        const p = ranParams || buildAdvanceReportParams(filters, roleId, userId);
        XLSX.writeFile(wb, `Labour_Advance_Report_${p.ccCode}_${period(p.fromMonth, p.fromYear)}_to_${period(p.toMonth, p.toYear)}.xlsx`);
        toast.success('Excel downloaded');
    };

    const cards = [
        { label: 'Labour', value: rows.length, tone: 'text-gray-900 dark:text-white' },
        { label: 'Opening', value: money(totals.Opening), tone: 'text-gray-900 dark:text-white' },
        { label: 'Advance Given', value: money(totals.AdvanceGiven), tone: 'text-amber-700 dark:text-amber-400' },
        { label: 'Paid by CMS', value: money(totals.CMSPaid), tone: 'text-blue-700 dark:text-blue-400' },
        { label: 'Recovered', value: money(totals.Recovered), tone: 'text-teal-700 dark:text-teal-400' },
        { label: 'Closing Balance', value: money(totals.Closing), tone: 'text-indigo-700 dark:text-indigo-300' },
    ];

    const th = 'px-2 py-1.5 text-[10px] font-bold text-white uppercase whitespace-nowrap';

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <HandCoins className="h-4 w-4 text-white" />
                            </div>
                            {menuData?.name || 'Labour Advance Report'}
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Bulk labour advance per labour and cost centre: opening, given, paid by CMS, recovered in payroll and balance
                        </p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Labour Advance Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={!rows.length} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                    <div className="col-span-2">
                        <Label>Cost Centre</Label>
                        <select className={inputCls} value={filters.ccCode} onChange={(e) => setField('ccCode', e.target.value)} disabled={reportCCsLoading}>
                            <option value="All">{reportCCsLoading ? 'Loading…' : 'All Cost Centres'}</option>
                            {reportCCs.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} - {c.CC_Name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>From Month</Label>
                        <select className={inputCls} value={filters.fromMonth} onChange={(e) => setField('fromMonth', Number(e.target.value))}>
                            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>From Year</Label>
                        <select className={inputCls} value={filters.fromYear} onChange={(e) => setField('fromYear', Number(e.target.value))}>
                            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>To Month</Label>
                        <select className={inputCls} value={filters.toMonth} onChange={(e) => setField('toMonth', Number(e.target.value))}>
                            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>To Year</Label>
                        <select className={inputCls} value={filters.toYear} onChange={(e) => setField('toYear', Number(e.target.value))}>
                            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end mt-3">
                    <div>
                        <Label>Labour Type</Label>
                        <select className={inputCls} value={filters.labourType} onChange={(e) => setField('labourType', e.target.value)}>
                            {LABOUR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Labour ID (optional)</Label>
                        <input className={inputCls} value={filters.labourId} placeholder="All labour"
                            onChange={(e) => setField('labourId', e.target.value)} />
                    </div>
                    <div className="col-span-2 flex items-center pb-1.5">
                        <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                            <input type="checkbox" className="accent-indigo-600" checked={filters.onlyWithBalance}
                                onChange={(e) => setField('onlyWithBalance', e.target.checked)} />
                            Only labour with a balance
                        </label>
                    </div>
                    <div className="col-span-2 flex gap-2 md:justify-end">
                        <button onClick={runReport} disabled={reportLoading} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : rows.length > 0 ? (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        {cards.map((c) => (
                            <div key={c.label} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{c.label}</p>
                                <p className={`text-sm font-bold mt-1 ${c.tone}`}>{c.value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
                            <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                Advance Ledger — {period(ranParams.fromMonth, ranParams.fromYear)} to {period(ranParams.toMonth, ranParams.toYear)}
                            </h2>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">Click a row for its advance and recovery lines</span>
                        </div>
                        <div className="overflow-auto max-h-[70vh]">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                    <tr>
                                        <th className="w-6" />
                                        {['CC', 'Labour ID', 'Name', 'Type', 'Contractor'].map((h) => <th key={h} className={`${th} text-left`}>{h}</th>)}
                                        {['Opening', 'Advance Given', 'Paid by CMS', 'Recovered', 'Closing'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {rows.map((r, idx) => {
                                        const open = expanded === idx;
                                        return (
                                            <React.Fragment key={`${r.CCCode}-${r.LabourId}`}>
                                                <tr onClick={() => setExpanded(open ? null : idx)}
                                                    className={`cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors ${open ? 'bg-indigo-50 dark:bg-indigo-900/10' : ''}`}>
                                                    <td className="px-1 text-center text-indigo-600">
                                                        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                    </td>
                                                    <td className="px-2 py-1 whitespace-nowrap" title={r.CCName}>{r.CCCode}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap font-semibold text-indigo-700 dark:text-indigo-300">{r.LabourId}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">{r.LabourName}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">{r.LabourType}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">{r.ContractorName || r.ContractorCode || ''}</td>
                                                    <td className="px-2 py-1 text-right whitespace-nowrap">{money(r.Opening)}</td>
                                                    <td className="px-2 py-1 text-right whitespace-nowrap">
                                                        {money(r.AdvanceGiven)}
                                                        {num(r.AdvancePending) > 0 && <div className="text-[10px] text-amber-600 dark:text-amber-400">{money(r.AdvancePending)} in approval</div>}
                                                    </td>
                                                    <td className="px-2 py-1 text-right whitespace-nowrap">{money(r.CMSPaid)}</td>
                                                    <td className="px-2 py-1 text-right whitespace-nowrap">
                                                        {money(r.Recovered)}
                                                        {num(r.RecoveryPending) > 0 && <div className="text-[10px] text-amber-600 dark:text-amber-400">{money(r.RecoveryPending)} payroll in approval</div>}
                                                    </td>
                                                    <td className={`px-2 py-1 text-right whitespace-nowrap font-bold ${num(r.Closing) > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}`}>
                                                        {money(r.Closing)}
                                                    </td>
                                                </tr>
                                                {open && (
                                                    <tr>
                                                        <td colSpan={11} className="bg-gray-50 dark:bg-gray-900/30 px-4 py-2">
                                                            <LedgerLines lines={advanceReportLinesFor(lines, r)} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="sticky bottom-0 z-10">
                                    <tr className="bg-gray-100 dark:bg-gray-900 font-bold border-t-2 border-gray-300 dark:border-gray-600">
                                        <td />
                                        <td colSpan={5} className="px-2 py-1.5 whitespace-nowrap">TOTAL ({rows.length} labour)</td>
                                        {['Opening', 'AdvanceGiven', 'CMSPaid', 'Recovered', 'Closing'].map((k) => (
                                            <td key={k} className={`px-2 py-1.5 text-right whitespace-nowrap ${k === 'Closing' ? 'text-indigo-700 dark:text-indigo-300' : ''}`}>{money(totals[k])}</td>
                                        ))}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default LabourAdvanceReport;
